/**
 * Card body dispatch. Returns the right Body component for the given
 * card type. Per-control inline edits dispatch through onEditField;
 * the shell wraps them into edit_field ResolveActions.
 *
 * Bodies live in cards/shell/bodies/. Unbuilt bodies fall through to
 * UnknownCardBody so the chat doesn't crash during incremental rollout.
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §5.
 */
import React from 'react';

import { CardType } from '../../types';

import { pickResultRenderer } from '../results/pickResultRenderer';

import { AmbiguityCardBody } from './bodies/AmbiguityCardBody';
import { ChartVisualizationParamBody } from './bodies/ChartVisualizationParamBody';
import { DriverAnalysisParamBody } from './bodies/DriverAnalysisParamBody';
import { EditScenariosChangeReviewBody } from './bodies/EditScenariosChangeReviewBody';
import { EditScenariosInputSelectionBody } from './bodies/EditScenariosInputSelectionBody';
import { PlanConfirmationBody } from './bodies/PlanConfirmationBody';
import { UnknownCardBody } from './bodies/UnknownCardBody';

export type EditFieldHandler = (field_updates: Record<string, unknown>) => void;

export interface CardBodyProps {
  cardType: CardType;
  content: Record<string, unknown>;
  /**
   * Called by editable bodies on per-control commit. Shell wraps into
   * { action: 'edit_field', field_updates } and dispatches /v2/cards/resolve.
   * Bodies for cards without editable fields can ignore this prop.
   */
  onEditField: EditFieldHandler;
  /** Disables editable controls while a /v2 request is in flight. */
  editsDisabled: boolean;
}

export function pickCardBody(props: CardBodyProps): React.ReactElement {
  const { cardType, content, onEditField, editsDisabled } = props;

  switch (cardType) {
    case 'plan_confirmation_card':
      return <PlanConfirmationBody content={content} />;

    case 'driver_analysis_param_card':
      return (
        <DriverAnalysisParamBody
          content={content}
          onEditField={onEditField}
          editsDisabled={editsDisabled}
        />
      );

    case 'chart_visualization_param_card':
      return (
        <ChartVisualizationParamBody
          content={content}
          onEditField={onEditField}
          editsDisabled={editsDisabled}
        />
      );

    case 'edit_scenarios_input_selection_card':
      return (
        <EditScenariosInputSelectionBody
          content={content}
          onEditField={onEditField}
          editsDisabled={editsDisabled}
        />
      );

    case 'edit_scenarios_change_review_card':
      return (
        <EditScenariosChangeReviewBody
          content={content}
          onEditField={onEditField}
          editsDisabled={editsDisabled}
        />
      );

    case 'result_card':
      return pickResultRenderer(content);

    case 'ambiguity_card':
      return (
        <AmbiguityCardBody
          content={content}
          onEditField={onEditField}
          editsDisabled={editsDisabled}
        />
      );

    // Bodies below not yet implemented — fall through to UnknownCardBody.
    case 'extracts_param_card':
    case 'scope_proposal_card':
    case 'explain_answer_card':
    default:
      return <UnknownCardBody cardType={cardType} content={content} />;
  }
}
